import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import CaseText from "@/components/CaseText";
import { ReadAloud } from "@/components/ReadAloud";
import api from "@/lib/api";
import { Library, Search, ChevronLeft, ChevronRight, Download, Loader2, X, CloudDownload, Check } from "lucide-react";
import { saveItem, getItem, listKeys } from "@/lib/offline";
import { toast } from "sonner";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Blacks() {
  const [meta, setMeta] = useState(null);
  const [letter, setLetter] = useState("A");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(new Set());
  const [savingOff, setSavingOff] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    api.get("/blacks/letters").then((r) => setMeta(r.data)).catch(() => {});
    listKeys().then((keys) => setSaved(new Set(keys.filter((k) => k.startsWith("blacks:")).map((k) => k.slice(7)))));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/blacks/${letter}?page=${page}&size=48`)
      .then((r) => setData(r.data))
      .catch(async () => {
        const c = await getItem(`blacks:${letter}`);
        if (c) setData({ letter, page: 0, total_pages: 1, total: c.length, entries: c.slice(0, 400), offline: true });
        else setData(null);
      })
      .finally(() => setLoading(false));
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [letter, page]);

  const saveOffline = async () => {
    setSavingOff(true);
    try {
      let all = [], p = 0, tp = 1;
      do {
        const r = await api.get(`/blacks/${letter}?page=${p}&size=200`);
        all = all.concat(r.data.entries); tp = r.data.total_pages; p++;
      } while (p < tp && p < 40);
      await saveItem(`blacks:${letter}`, all);
      setSaved((s) => new Set(s).add(letter));
      toast.success(`“${letter}” saved for offline (${all.length} terms)`);
    } catch { toast.error("Could not save offline"); }
    finally { setSavingOff(false); }
  };

  const countFor = useMemo(() => {
    const m = {};
    (meta?.letters || []).forEach((l) => { m[l.letter] = l.count; });
    return m;
  }, [meta]);

  const [searchParams] = useSearchParams();

  const runSearchTerm = async (term) => {
    if (!term || term.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      const r = await api.get(`/blacks/search?q=${encodeURIComponent(term.trim())}`);
      setResults(r.data.results);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  useEffect(() => {
    const qq = searchParams.get("q");
    if (qq) { setQ(qq); runSearchTerm(qq); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const runSearch = async (e) => {
    e?.preventDefault();
    runSearchTerm(q);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/blacks/${letter}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url; a.download = `blacks-law-4th-${letter}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const Card = ({ e }) => (
    <div className="bg-card border border-border p-6" data-testid={`blacks-term-${e.term.replace(/\s/g, "-").toLowerCase().slice(0, 40)}`}>
      <div className="flex items-start justify-between mb-2 gap-3">
        <h3 className="font-mono text-lg text-foreground leading-tight">{e.term}</h3>
        <div className="flex items-center gap-2 shrink-0">
          <ReadAloud label="" getText={() => `${e.term}. ${e.definition}`} />
          <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground border border-border px-2 py-0.5">Black's 4th</span>
        </div>
      </div>
      <p className="text-sm text-foreground/85 leading-relaxed"><CaseText text={e.definition} /></p>
      <a href={`/support?category=content&subject=${encodeURIComponent(`Typo in "${e.term}"`)}&message=${encodeURIComponent(`I think the definition for "${e.term}" in Black's Law has a typo or garbled text:\n\n`)}`}
        data-testid="blacks-report-typo"
        className="mt-3 inline-block text-[11px] text-muted-foreground/70 hover:text-primary transition-colors">
        Report a typo
      </a>
    </div>
  );

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-14" ref={topRef}>
        <span className="text-xs uppercase tracking-widest font-mono text-primary">Reference · Complete</span>
        <h1 className="font-serif text-5xl mt-2 mb-3">Black's Law Dictionary</h1>
        <p className="text-muted-foreground max-w-2xl mb-8">
          {meta?.source || "Revised 4th edition (1968)"} — the complete work, {meta ? meta.total.toLocaleString() : "24,000+"} terms,
          organized A–Z. Search everything, browse by letter, and download any section for offline study.
        </p>

        <form onSubmit={runSearch} className="relative mb-6 max-w-2xl">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="blacks-search-input" value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value) setResults(null); }}
            placeholder="Search all 24,000+ terms & definitions…"
            className="w-full bg-muted border border-input pl-10 pr-24 py-3 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />
          <button type="submit" data-testid="blacks-search-btn" disabled={searching}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-sm text-xs flex items-center gap-1.5">
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Search
          </button>
        </form>

        {results !== null ? (
          <div data-testid="blacks-search-results">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl">{results.length}{results.length === 80 ? "+" : ""} result{results.length !== 1 ? "s" : ""} for “{q}”</h2>
              <button onClick={() => { setResults(null); setQ(""); }} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"><X className="w-4 h-4" /> Clear</button>
            </div>
            {results.length === 0 ? (
              <p className="text-muted-foreground">No terms match your search.</p>
            ) : (
              <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                {results.map((e, i) => <Card key={i} e={e} />)}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-8" data-testid="blacks-az-rail">
              {ALPHABET.map((L) => {
                const has = countFor[L];
                return (
                  <button key={L} disabled={!has} onClick={() => { setLetter(L); setPage(0); }}
                    data-testid={`blacks-letter-${L}`}
                    className={`w-10 h-10 rounded-sm text-sm font-serif transition-colors ${
                      letter === L ? "bg-primary text-primary-foreground" :
                      has ? "bg-card border border-border text-foreground hover:border-primary hover:text-primary" :
                      "bg-muted/40 text-muted-foreground/40 cursor-not-allowed"
                    }`}>
                    {L}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between mb-4 border-b border-border pb-2">
              <h2 className="font-serif text-3xl text-primary">
                {letter} <span className="text-base font-mono text-muted-foreground ml-2">{data ? `${data.total.toLocaleString()} terms` : ""}</span>
              </h2>
              <div className="flex items-center gap-4">
                <button onClick={saveOffline} disabled={savingOff} data-testid="blacks-save-offline"
                  className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors">
                  {savingOff ? <Loader2 className="w-4 h-4 animate-spin" /> : saved.has(letter) ? <Check className="w-4 h-4 text-primary" /> : <CloudDownload className="w-4 h-4" />}
                  {saved.has(letter) ? "Saved offline" : "Save offline"}
                </button>
                <button onClick={download} disabled={downloading} data-testid="blacks-download"
                  className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download “{letter}” (.txt)
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="w-7 h-7 animate-spin" /></div>
            ) : (
              <div className="grid gap-4" data-testid="blacks-cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                {data?.entries.map((e, i) => <Card key={i} e={e} />)}
              </div>
            )}

            {data && data.total_pages > 1 && (
              <div className="flex items-center justify-center gap-6 mt-10">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} data-testid="blacks-prev"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary disabled:opacity-40 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="text-xs font-mono text-muted-foreground">Page {data.page + 1} / {data.total_pages}</span>
                <button onClick={() => setPage((p) => (data ? Math.min(data.total_pages - 1, p + 1) : p))}
                  disabled={data.page >= data.total_pages - 1} data-testid="blacks-next"
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary disabled:opacity-40 transition-colors">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        <p className="text-xs text-muted-foreground font-mono mt-10">
          For educational purposes only. None of this shall be construed as legal advice.
        </p>
      </div>
    </PageShell>
  );
}
